import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import PrimaryButton from '../../../components/PrimaryButton';
import client from '../../../lib/feathers';
import NewHolidayForm from '../../../components/data-management/NewHolidayForm';
import DeleteConfirmationModal from '../../../components/data-management/DeleteConfirmationModal';

function formatHolidayDate(dateStr) {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export default function Holidays() {
  const [holidays, setHolidays] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [deletingHoliday, setDeletingHoliday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generateYear, setGenerateYear] = useState(new Date().getFullYear());
  const [generating, setGenerating] = useState(false);

  const getHolidays = async () => {
    try {
      setLoading(true);
      const service = client.service('admin/holiday');
      const result = await service.find({
        query: {
          $limit: 1000,
          $sort: { date: 1 },
        },
      });
      setHolidays(result.data);
    } catch (e) {
      console.error('Error fetching holidays:', e);
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getHolidays();
  }, []);

  const onNewHoliday = (newHoliday) => {
    setHolidays([newHoliday, ...holidays].sort((a, b) => new Date(a.date) - new Date(b.date)));
  };

  const onUpdateHoliday = (updatedHoliday) => {
    setHolidays(
      holidays
        .map((h) => (h.id === updatedHoliday.id ? updatedHoliday : h))
        .sort((a, b) => new Date(a.date) - new Date(b.date))
    );
    setEditingHoliday(null);
  };

  const handleDelete = async () => {
    if (!deletingHoliday) return;
    try {
      const service = client.service('admin/holiday');
      await service.remove(deletingHoliday.id);
      setHolidays(holidays.filter((h) => h.id !== deletingHoliday.id));
      toast.success(`Deleted ${deletingHoliday.name}`);
      setDeletingHoliday(null);
    } catch (e) {
      console.error('Error deleting holiday:', e);
      toast.error('Failed to delete holiday');
    }
  };

  const handleToggleActive = async (holiday) => {
    try {
      const service = client.service('admin/holiday');
      const updated = await service.patch(holiday.id, { active: !holiday.active });
      setHolidays(
        holidays.map((h) => (h.id === holiday.id ? updated : h))
      );
      toast.success(`${updated.name} is now ${updated.active ? 'active' : 'inactive'}`);
    } catch (e) {
      console.error('Error updating holiday:', e);
      toast.error('Failed to update holiday');
    }
  };

  const handleGenerateHolidays = async () => {
    try {
      setGenerating(true);
      const service = client.service('admin/holiday');
      await service.create({ action: 'generate', year: generateYear });
      toast.success(`Generated holidays for ${generateYear}`);
      setShowGenerateModal(false);
      await getHolidays();
    } catch (e) {
      console.error('Error generating holidays:', e);
      toast.error('Failed to generate holidays');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div>
      <div className="sm:flex sm:items-center sm:justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Holidays</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setShowGenerateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Generate Holidays
          </button>
          <PrimaryButton onClick={() => setShowCreateModal(true)}>
            New Holiday
          </PrimaryButton>
        </div>
      </div>

      <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Name
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Date
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Active
              </th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : holidays.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                  No holidays found
                </td>
              </tr>
            ) : (
              holidays.map((holiday) => (
                <tr key={holiday.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {holiday.name}
                    </div>
                    {holiday.description && (
                      <div className="text-sm text-gray-500">
                        {holiday.description}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatHolidayDate(holiday.date)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleActive(holiday)}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${
                        holiday.active
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                    >
                      {holiday.active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                    <button
                      onClick={() => setEditingHoliday(holiday)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setDeletingHoliday(holiday)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" />
            </div>
            <div
              className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6"
              role="dialog"
              aria-modal="true"
            >
              <NewHolidayForm
                onCancelClick={() => setShowCreateModal(false)}
                onNewHoliday={(newHoliday) => {
                  onNewHoliday(newHoliday);
                  setShowCreateModal(false);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingHoliday && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" />
            </div>
            <div
              className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6"
              role="dialog"
              aria-modal="true"
            >
              <NewHolidayForm
                holiday={editingHoliday}
                onCancelClick={() => setEditingHoliday(null)}
                onUpdateHoliday={onUpdateHoliday}
              />
            </div>
          </div>
        </div>
      )}

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" />
            </div>
            <div
              className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6"
              role="dialog"
              aria-modal="true"
            >
              <div className="mt-3 text-center sm:mt-5">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Generate Holidays
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Generate standard US holidays for a specific year.
                  </p>
                  <div className="mt-4">
                    <label htmlFor="year" className="block text-sm font-medium text-gray-700 text-left">
                      Year
                    </label>
                    <input
                      type="number"
                      value={generateYear}
                      onChange={(e) => setGenerateYear(parseInt(e.target.value))}
                      min="2020"
                      max="2099"
                      name="year"
                      id="year"
                      className="mt-1 focus:ring-gray-500 focus:border-gray-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  onClick={handleGenerateHolidays}
                  disabled={generating}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-red-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generating ? 'Generating...' : 'Generate'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowGenerateModal(false)}
                  disabled={generating}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:col-start-1 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingHoliday && (
        <DeleteConfirmationModal
          title="Delete Holiday"
          message={`Are you sure you want to delete "${deletingHoliday.name}"? This action cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setDeletingHoliday(null)}
        />
      )}
    </div>
  );
}
